// ============================================================
// CANONICAL TAG DATABASE — single source of truth for all
// book-related tag types. Pages and components should import
// from here instead of maintaining their own hardcoded arrays.
// ============================================================

// ── Types ────────────────────────────────────────────────────

export interface Genre {
  id: string
  name: string
}

export interface Subgenre {
  id: string
  name: string
  genreId: string // parent genre
}

export interface Trope {
  id: string
  name: string
  genreIds: string[] // genres this trope appears in
  subgenreIds: string[] // subgenres this trope appears in
  description?: string
}

export interface Theme {
  id: string
  name: string
}

export interface Character {
  id: string
  name: string
}

export interface Setting {
  id: string
  name: string
}

export interface Representation {
  id: string
  name: string
}

export interface CulturalIdentity {
  id: string
  name: string
}

export interface ContentWarning {
  id: string
  name: string
}

export interface POVType {
  id: string
  name: string
  description: string
}

export interface TenseType {
  id: string
  name: string
  description: string
}

// ── Book-only tag types ───────────────────────────────────────

export interface RelationshipType {
  id: string
  name: string
}

export interface BurnLevel {
  id: string
  name: string
  level: 1 | 2 | 3
}

export interface SpiceLevel {
  id: string
  name: string
  level: 1 | 2 | 3 | 4 | 5
  description: string
}

export interface DarknessLevel {
  id: string
  name: string
  level: 1 | 2 | 3 | 4 | 5
  description: string
}

export interface EndingType {
  id: string
  name: string
}

// ── Genres ───────────────────────────────────────────────────

export const genres: Genre[] = [
  { id: 'romance', name: 'Romance' },
  { id: 'fantasy', name: 'Fantasy' },
  { id: 'thriller', name: 'Thriller' },
]

// ── Subgenres ─────────────────────────────────────────────────
// Each subgenre links to exactly one parent genre via genreId.

export const subgenres: Subgenre[] = [
  {
    id: 'contemporary-romance',
    name: 'Contemporary Romance',
    genreId: 'romance',
  },
  { id: 'dark-romance', name: 'Dark Romance', genreId: 'romance' },
  { id: 'epic-fantasy', name: 'Epic Fantasy', genreId: 'fantasy' },
  {
    id: 'psychological-thriller',
    name: 'Psychological Thriller',
    genreId: 'thriller',
  },
]

// ── Tropes ────────────────────────────────────────────────────
// Each trope links to one or more genres AND one or more subgenres.

export const tropes: Trope[] = [
  {
    id: 'enemies-to-lovers',
    name: 'Enemies to Lovers',
    genreIds: ['romance', 'fantasy'],
    subgenreIds: ['contemporary-romance', 'dark-romance'],
    description:
      'Characters who initially dislike or hate each other eventually fall in love.',
  },
  {
    id: 'forced-proximity',
    name: 'Forced Proximity',
    genreIds: ['romance', 'thriller'],
    subgenreIds: ['contemporary-romance', 'dark-romance'],
    description:
      'Characters are forced to spend time together in close quarters.',
  },
  {
    id: 'revenge-romance',
    name: 'Revenge Romance',
    genreIds: ['romance'],
    subgenreIds: ['dark-romance'],
    description: 'A romance driven by a revenge plot or vendetta.',
  },
  {
    id: 'chosen-one',
    name: 'Chosen One',
    genreIds: ['fantasy'],
    subgenreIds: ['epic-fantasy'],
    description:
      'A character is destined to save the world or fulfill a prophecy.',
  },
  {
    id: 'unreliable-narrator',
    name: 'Unreliable Narrator',
    genreIds: ['thriller'],
    subgenreIds: ['psychological-thriller'],
    description: 'The narrator of the story cannot be fully trusted.',
  },
  // Additional common tropes
  {
    id: 'friends-to-lovers',
    name: 'Friends to Lovers',
    genreIds: ['romance'],
    subgenreIds: ['contemporary-romance'],
    description:
      'Characters who are friends first develop romantic feelings for each other.',
  },
  {
    id: 'fake-dating',
    name: 'Fake Dating',
    genreIds: ['romance'],
    subgenreIds: ['contemporary-romance'],
    description:
      'Characters pretend to be in a relationship but eventually develop real feelings.',
  },
  {
    id: 'slow-burn',
    name: 'Slow Burn',
    genreIds: ['romance', 'fantasy'],
    subgenreIds: ['contemporary-romance', 'dark-romance', 'epic-fantasy'],
    description: 'Romantic tension builds slowly throughout the story.',
  },
  {
    id: 'forbidden-love',
    name: 'Forbidden Love',
    genreIds: ['romance', 'fantasy'],
    subgenreIds: ['dark-romance', 'epic-fantasy'],
    description: 'Characters fall in love despite societal or other barriers.',
  },
  {
    id: 'found-family',
    name: 'Found Family',
    genreIds: ['fantasy'],
    subgenreIds: ['epic-fantasy'],
    description:
      'Characters form deep bonds that resemble family relationships.',
  },
  {
    id: 'locked-room-mystery',
    name: 'Locked Room Mystery',
    genreIds: ['thriller'],
    subgenreIds: ['psychological-thriller'],
    description:
      'A crime committed in a sealed environment with no apparent way in or out.',
  },
]

// ── Themes ────────────────────────────────────────────────────

export const themes: Theme[] = [
  { id: 'coming-of-age', name: 'Coming of Age' },
  { id: 'redemption', name: 'Redemption' },
  { id: 'identity', name: 'Identity' },
  { id: 'grief-and-loss', name: 'Grief & Loss' },
  // Additional themes
  { id: 'power', name: 'Power' },
  { id: 'morality', name: 'Morality' },
  { id: 'destiny', name: 'Destiny' },
  { id: 'survival', name: 'Survival' },
]

// ── Characters ────────────────────────────────────────────────

export const characters: Character[] = [
  { id: 'strong-female-lead', name: 'Strong Female Lead' },
  { id: 'brooding-hero', name: 'Brooding Hero' },
  { id: 'morally-grey-antihero', name: 'Morally Grey Antihero' },
  { id: 'reluctant-hero', name: 'Reluctant Hero' },
  { id: 'unreliable-narrator-char', name: 'Unreliable Narrator' },
  // Additional characters
  { id: 'anti-hero', name: 'Anti-Hero' },
  { id: 'chosen-one-char', name: 'Chosen One' },
  { id: 'mentor', name: 'Mentor' },
]

// ── Settings ──────────────────────────────────────────────────

export const settings: Setting[] = [
  { id: 'small-town', name: 'Small Town' },
  { id: 'big-city', name: 'Big City' },
  { id: 'fantasy-world', name: 'Fantasy World' },
  { id: 'isolated-mansion', name: 'Isolated Mansion' },
  // Additional settings
  { id: 'historical', name: 'Historical' },
  { id: 'space', name: 'Space' },
  { id: 'school-academy', name: 'School / Academy' },
  { id: 'small-town-rural', name: 'Rural / Countryside' },
]

// ── Representation ────────────────────────────────────────────

export const representation: Representation[] = [
  { id: 'lgbtq', name: 'LGBTQ+' },
  { id: 'bipoc', name: 'BIPOC' },
  { id: 'neurodivergent', name: 'Neurodivergent' },
  // Additional representation
  { id: 'disability', name: 'Disability' },
  { id: 'plus-size', name: 'Plus Size' },
  { id: 'mental-health', name: 'Mental Health' },
]

// ── Cultural Identity ─────────────────────────────────────────

export const culturalIdentities: CulturalIdentity[] = [
  { id: 'african-american', name: 'African American' },
  { id: 'latinx', name: 'Latinx' },
  { id: 'asian', name: 'Asian' },
  { id: 'south-asian', name: 'South Asian' },
  { id: 'indigenous', name: 'Indigenous' },
  { id: 'jewish', name: 'Jewish' },
  { id: 'muslim', name: 'Muslim' },
  { id: 'middle-eastern', name: 'Middle Eastern' },
  { id: 'pacific-islander', name: 'Pacific Islander' },
  { id: 'mixed-race', name: 'Mixed Race' },
  { id: 'african', name: 'African' },
  { id: 'caribbean', name: 'Caribbean' },
  { id: 'east-asian', name: 'East Asian' },
  { id: 'southeast-asian', name: 'Southeast Asian' },
]

// ── Content Warnings ──────────────────────────────────────────

export const contentWarnings: ContentWarning[] = [
  { id: 'violence', name: 'Violence' },
  { id: 'sexual-content', name: 'Sexual Content' },
  { id: 'addiction', name: 'Addiction' },
  { id: 'kidnapping', name: 'Kidnapping' },
  // Additional warnings
  { id: 'strong-language', name: 'Strong Language' },
  { id: 'psychological', name: 'Psychological Trauma' },
  { id: 'grief', name: 'Grief & Loss' },
  { id: 'abuse', name: 'Abuse' },
  { id: 'suicide', name: 'References to Suicide' },
]

// ── Relationship Types (books only) ───────────────────────────

export const relationshipTypes: RelationshipType[] = [
  { id: 'mf', name: 'MF' },
  { id: 'mm', name: 'MM' },
  { id: 'ff', name: 'FF' },
  { id: 'why-choose', name: 'Why Choose' },
  { id: 'none', name: 'None' },
]

// ── Burn Levels (books only) ──────────────────────────────────

export const burnLevels: BurnLevel[] = [
  { id: 'slow', name: 'Slow', level: 1 },
  { id: 'medium', name: 'Medium', level: 2 },
  { id: 'fast', name: 'Fast', level: 3 },
]

// ── Spice Levels (books only) ─────────────────────────────────

export const spiceLevels: SpiceLevel[] = [
  {
    id: 'mild',
    name: 'Mild',
    level: 1,
    description: 'Sweet kisses and no on-page intimacy.',
  },
  {
    id: 'warm',
    name: 'Warm',
    level: 2,
    description: 'Open door with fade-to-black intimacy.',
  },
  {
    id: 'spicy',
    name: 'Spicy',
    level: 3,
    description: 'On-page explicit scenes.',
  },
  {
    id: 'extra-hot',
    name: 'Extra Hot',
    level: 4,
    description: 'Multiple explicit scenes including kink.',
  },
  {
    id: 'inferno',
    name: 'Inferno',
    level: 5,
    description: 'Explicit erotica-driven content.',
  },
]

// ── Darkness Levels (books only) ──────────────────────────────

export const darknessLevels: DarknessLevel[] = [
  {
    id: 'hint-of-shadow',
    name: 'Hint of Shadow',
    level: 1,
    description: 'Light emotional tension.',
  },
  {
    id: 'into-the-grey',
    name: 'Into the Grey',
    level: 2,
    description: 'Moderate emotional weight.',
  },
  {
    id: 'embrace-the-abyss',
    name: 'Embrace the Abyss',
    level: 3,
    description: 'Present dark themes with moderate graphic content.',
  },
  {
    id: 'pitch-black',
    name: 'Pitch Black',
    level: 4,
    description: 'Heavy psychological darkness and manipulation.',
  },
  {
    id: 'the-void',
    name: 'The Void',
    level: 5,
    description: 'Extremely graphic and triggering content.',
  },
]

// ── Ending Types (books only) ─────────────────────────────────

export const endingTypes: EndingType[] = [
  { id: 'hea', name: 'HEA' },
  { id: 'hfn', name: 'HFN' },
  { id: 'cliffhanger', name: 'Cliffhanger' },
  { id: 'tragic', name: 'Tragic' },
  { id: 'bittersweet', name: 'Bittersweet' },
]

// ── POV Types (books only) ────────────────────────────────────

export const povTypes: POVType[] = [
  {
    id: 'first-person',
    name: 'First Person',
    description: 'Story told from "I" perspective.',
  },
  {
    id: 'third-person-limited',
    name: 'Third Person Limited',
    description: "Follows one character's thoughts at a time.",
  },
  {
    id: 'third-person-omniscient',
    name: 'Third Person Omniscient',
    description:
      "All-knowing narrator with access to every character's thoughts.",
  },
  {
    id: 'dual-pov',
    name: 'Dual POV',
    description: 'Alternates between two character perspectives.',
  },
  {
    id: 'multi-pov',
    name: 'Multi POV',
    description: 'Three or more character perspectives.',
  },
  {
    id: 'alternating-pov',
    name: 'Alternating POV',
    description: 'Switches between characters across chapters.',
  },
  {
    id: 'second-person',
    name: 'Second Person',
    description: 'Story told from "you" perspective.',
  },
]

// ── Tense Types (books only) ──────────────────────────────────

export const tenseTypes: TenseType[] = [
  {
    id: 'past-tense',
    name: 'Past Tense',
    description: 'Story narrated in past tense ("she walked").',
  },
  {
    id: 'present-tense',
    name: 'Present Tense',
    description: 'Story narrated in present tense ("she walks").',
  },
  {
    id: 'mixed-tense',
    name: 'Mixed Tense',
    description: 'Alternates between past and present tense across sections.',
  },
]

// ── Lookup helpers ────────────────────────────────────────────

export const getGenreById = (id: string) => genres.find((g) => g.id === id)

export const getSubgenreById = (id: string) =>
  subgenres.find((s) => s.id === id)

export const getSubgenresByGenre = (genreId: string) =>
  subgenres.filter((s) => s.genreId === genreId)

export const getTropeById = (id: string) => tropes.find((t) => t.id === id)

export const getTropesByGenre = (genreId: string) =>
  tropes.filter((t) => t.genreIds.includes(genreId))

export const getTropesBySubgenre = (subgenreId: string) =>
  tropes.filter((t) => t.subgenreIds.includes(subgenreId))

export const getSpiceLevelByValue = (level: number) =>
  spiceLevels.find((s) => s.level === level)

export const getDarknessLevelByValue = (level: number) =>
  darknessLevels.find((d) => d.level === level)

export const getBurnLevelByValue = (level: number) =>
  burnLevels.find((b) => b.level === level)

export const getPOVTypeById = (id: string) => povTypes.find((p) => p.id === id)

export const getTenseTypeById = (id: string) =>
  tenseTypes.find((t) => t.id === id)
